import classNames from 'classnames';
import CopyToClipboard from 'legacy/common/components/copy-to-clipboard/CopyToClipboard';
import Toggle, { ToggleSize } from 'legacy/common/components/form/Toggle';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import useParams from 'legacy/common/hooks/useParams';
import {
  useCreateDiscountCode,
  useDeleteDiscountCode,
  useGetDiscountCodes,
  useGetDiscountCodesAnalytics,
  useGetEvent,
  useUpdateDiscountCode
} from 'legacy/events/hooks';
import {
  EventDiscountCodesDto,
  DiscountCodeDto,
  DiscountCodeAnalyticsDto,
  CreateDiscountCodeBody
} from 'legacy/events/types';
import { isBounceWebview, postMessageToRN } from 'legacy/common/utils/webview';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { BiLeftArrowAlt } from 'react-icons/bi';
import { FaMoneyBillAlt, FaTags } from 'react-icons/fa';
import { TbTicket } from 'react-icons/tb';
import { showModal } from 'legacy/actions/modalActions';
import { useDispatch } from 'react-redux';
import { BsThreeDots } from 'react-icons/bs';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import DiscountCodeForm from 'legacy/events/components/discount-codes/DiscountCodeForm';
import { useQueryClient } from 'react-query';
import { currencyFormat } from 'legacy/common/utils/currency';
import DiscountCodesActions from 'legacy/events/components/discount-codes/DiscountCodesActions';
import DiscountCodeEmptyState from 'legacy/events/components/event-page/DiscountCodeEmptyState';
import { WEBVIEW_CLOSE } from 'legacy/common/constants/webview';
import Redirect from 'legacy/components/util/Redirect/Redirect';

const DiscountCodeActionsMenu = ({
  code,
  onEditButtonClick,
  onDeleteButtonClick,
  onClose
}) => (
  <Modal visible type={ModalType.ALERT} onClose={onClose}>
    <div className="m-8">
      <div className="flex items-center text-ellipsis overflow-hidden">
        <FaTags className="mr-2" size={24} />
        <h2 className="text-base font-semibold">{code}</h2>
      </div>
      <div className="mt-8">
        <Button type={ButtonType.Primary} onClick={onEditButtonClick}>
          Edit Discount Code
        </Button>
      </div>
      <div className="mt-4">
        <Button type={ButtonType.Danger} onClick={onDeleteButtonClick}>
          Delete Discount Code
        </Button>
      </div>
    </div>
  </Modal>
);

const DiscountCodeCardSkeleton = () => (
  <div className="mt-6 bg-white shadow-xl rounded-2xl border-gray-100 border-1 animate-pulse">
    <div className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center text-ellipsis overflow-hidden">
          <FaTags className="mr-2" size={24} />
          <div className="text-3xl font-semibold h-3 w-20 bg-gray-300 rounded-full" />
        </div>
      </div>
      <div className="flex mt-3 items-center">
        <div className="font-semibold h-3 w-20 bg-gray-300 rounded-full" />
      </div>
    </div>

    <div className="flex bg-primary-200 justify-between p-6">
      <div className="mt-2 text-3xl font-semibold h-12 w-12 bg-gray-300 rounded-full" />
      <div className="mt-2 text-3xl font-semibold h-12 w-12 bg-gray-300 rounded-full" />
    </div>
  </div>
);

const DiscountCodeCard = ({
  discountCode,
  analytics,
  onEdit,
  onDelete
}: {
  discountCode: DiscountCodeDto;
  analytics?: DiscountCodeAnalyticsDto;
  onEdit: (discountCode: DiscountCodeDto, onSuccess?: () => void) => void;
  onDelete: (discountCode: DiscountCodeDto) => void;
}) => {
  const dispatch = useDispatch();

  const link = `${window.location.origin}/events/${discountCode.eventId}?discountCode=${discountCode.code}`;

  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const handleOpenActionsMenu = () => setShowActionsMenu(true);
  const handleCloseActionsMenu = () => setShowActionsMenu(false);

  const handleOpenEditForm = () => {
    setShowActionsMenu(false);
    setShowEditForm(true);
  };

  const handleCloseEditForm = () => setShowEditForm(false);

  const handleOpenDeleteConfirmation = () => {
    onDelete(discountCode);
  };

  return (
    <>
      <div className="mt-6 bg-white shadow-xl rounded-2xl border-gray-100 border-1">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-ellipsis overflow-hidden">
              <FaTags className="mr-2" size={24} />
              <h2 className="text-base font-semibold">{discountCode.code}</h2>
            </div>
            <div>
              <Button
                type={ButtonType.Text}
                icon={<BsThreeDots className="text-gray-400" size={24} />}
                onClick={handleOpenActionsMenu}
              />
            </div>
          </div>
          <div className="flex mt-3 items-center">
            <div className="w-5/6 border-1 border-gray-200 rounded-2xl px-4 py-2">
              <input className="w-full text-gray-400 truncate" value={link} />
            </div>
            <div className="w-1/6 ml-4">
              <CopyToClipboard message="Copy" copyText={link} />
            </div>
          </div>
          <div className="mt-3 flex items-center">
            {discountCode.isActive ? (
              <div className="mx-1 text-primary-500 font-normal">Active</div>
            ) : (
              <div className="mx-1 text-gray-400 font-normal">Paused</div>
            )}

            <div className="mt-1 mb-1 cursor-pointer">
              <Toggle
                size={ToggleSize.MEDIUM}
                isActive={discountCode.isActive}
                onClick={(e) => {
                  e.stopPropagation();
                  if (discountCode.isActive) {
                    onEdit({
                      ...discountCode,
                      isActive: false
                    });
                    return;
                  }
                  onEdit({
                    ...discountCode,
                    isActive: true
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex bg-primary-200 justify-between p-6">
          <div>
            <div className="text-base flex items-center font-normal">
              <FaMoneyBillAlt className="mr-1 text-lg font-normal" />
              Discount
            </div>
            <div className="mt-2 text-3xl font-semibold">
              {discountCode.discount.kind === 'amount' && '$'}
              {discountCode.discount.value || 0}
              {discountCode.discount.kind === 'percentage' && '%'}
            </div>
          </div>

          <div>
            <div className="text-base flex items-center font-normal">
              <TbTicket className="mr-1 text-lg" />
              Sales
            </div>
            <div className="mt-2 text-3xl font-semibold">
              {discountCode.numberOfUses || 0}
              {!!discountCode.maxUsage && (
                <span className="text-xl">/{discountCode.maxUsage}</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-base flex items-center font-normal">
              <img src="/img/events/cash.svg" className="mr-1" />
              Revenue
            </div>
            <div className="mt-2 text-3xl font-semibold">
              {currencyFormat(analytics?.revenue)}
            </div>
          </div>
        </div>
      </div>

      {showActionsMenu && (
        <DiscountCodeActionsMenu
          code={discountCode.code}
          onEditButtonClick={handleOpenEditForm}
          onDeleteButtonClick={handleOpenDeleteConfirmation}
          onClose={handleCloseActionsMenu}
        />
      )}
      {showEditForm && (
        <DiscountCodeForm
          initialValues={discountCode}
          title="Edit Discount Code"
          eventId={discountCode.eventId}
          onDelete={handleOpenDeleteConfirmation}
          onSubmit={(newDiscountCode) => {
            onEdit(
              {
                ...discountCode,
                ...newDiscountCode,
                discount: {
                  value: newDiscountCode.discount.value,
                  kind: newDiscountCode.discount.kind as 'amount' | 'percentage'
                }
              },
              () => {
                dispatch(
                  showModal('ALERT', {
                    type: 'success',
                    message: `Successfully modified discount code: ${newDiscountCode.code}`
                  })
                );
              }
            );
            handleCloseEditForm();
          }}
          onClose={handleCloseEditForm}
        />
      )}
    </>
  );
};

const DiscountCodesPage = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const params = useParams<{ slug: string; type: string }>();
  const eventId = params.slug;
  const { data: event, isLoading: isEventLoading } = useGetEvent(eventId);

  const [shouldShowCreateForm, setShouldShowCreateForm] = useState(false);

  useEffect(() => {
    if (params.type === 'new') {
      setShouldShowCreateForm(true);
    }
  }, []);

  const onBack = () => {
    if (isBounceWebview(router.query)) {
      postMessageToRN(WEBVIEW_CLOSE);
    } else {
      router.back();
    }
  };

  const handleCloseCreateForm = () => setShouldShowCreateForm(false);
  const handleOpenCreateForm = () => setShouldShowCreateForm(true);

  const {
    data: discountCodesResult,
    isLoading,
    error
  } = useGetDiscountCodes(eventId, {
    enabled: true
  });

  const {
    data: discountCodeAnalyticsResult,
    isLoading: isLoadingAnalytics,
    error: getAnalyticsError
  } = useGetDiscountCodesAnalytics(eventId, {
    enabled: true
  });

  const analyticsMap = useMemo(() => {
    if (isLoadingAnalytics || !!getAnalyticsError) {
      return {};
    }

    return discountCodeAnalyticsResult.reduce((map, analytics) => {
      map[analytics.discountCodeId] = analytics;
      return map;
    }, {});
  }, [isLoadingAnalytics, getAnalyticsError, discountCodeAnalyticsResult]);

  const createDiscountCode = useCreateDiscountCode();
  const updateDiscountCode = useUpdateDiscountCode();
  const deleteDiscountCode = useDeleteDiscountCode();

  const handleCreateDiscountCode = (discountCode: CreateDiscountCodeBody) => {
    createDiscountCode.mutate(
      {
        eventId,
        body: discountCode
      },
      {
        onSuccess: (newDiscountCode) => {
          const previousData: EventDiscountCodesDto = queryClient.getQueryData([
            'events/discountCodes',
            newDiscountCode.eventId
          ]);
          queryClient.setQueryData(
            ['events/discountCodes', newDiscountCode.eventId],
            {
              ...previousData,
              discountCodes: [...previousData.discountCodes, newDiscountCode]
            }
          );
          handleCloseCreateForm();
          dispatch(
            showModal('ALERT', {
              type: 'success',
              message: `Successfully created discount code: ${newDiscountCode.code}`
            })
          );
        },
        onError: (error) => {
          dispatch(
            showModal('ALERT', {
              message: error?.message
            })
          );
        }
      }
    );
  };

  const handleUpdateDiscountCode = (
    discountCode: DiscountCodeDto,
    onSuccess: () => void
  ) => {
    updateDiscountCode.mutate(
      {
        eventId: discountCode.eventId,
        discountCodeId: discountCode.id,
        body: {
          code: discountCode.code,
          discount: discountCode.discount,
          ...(discountCode.maxUsage && { maxUsage: discountCode.maxUsage }),
          ...(discountCode.startDate && { startDate: discountCode.startDate }),
          ...(discountCode.endDate && { endDate: discountCode.endDate }),
          ...(discountCode.tierIds && { tierIds: discountCode.tierIds }),
          isActive: discountCode.isActive
        }
      },
      {
        onSuccess: (newDiscountCode) => {
          const previousData: EventDiscountCodesDto = queryClient.getQueryData([
            'events/discountCodes',
            newDiscountCode.eventId
          ]);
          queryClient.setQueryData(
            ['events/discountCodes', newDiscountCode.eventId],
            {
              ...previousData,
              discountCodes: previousData.discountCodes.map(
                (previousDiscountCode) => {
                  if (newDiscountCode.id === previousDiscountCode.id) {
                    return newDiscountCode;
                  }
                  return previousDiscountCode;
                }
              )
            }
          );

          if (onSuccess) {
            onSuccess();
          }
        },
        onError: (error) => {
          dispatch(
            showModal('ALERT', {
              message: error?.message
            })
          );
        }
      }
    );
  };

  const handleDeleteDiscountCode = (discountCode: DiscountCodeDto) => {
    deleteDiscountCode.mutate(
      {
        eventId: discountCode.eventId,
        discountCodeId: discountCode.id
      },
      {
        onSuccess: () => {
          const previousData: EventDiscountCodesDto = queryClient.getQueryData([
            'events/discountCodes',
            eventId
          ]);
          queryClient.setQueryData(['events/discountCodes', eventId], {
            ...previousData,
            discountCodes: previousData.discountCodes.filter(
              (previousDiscountCode) =>
                previousDiscountCode.id !== discountCode.id
            )
          });
          dispatch(
            showModal('ALERT', {
              type: 'success',
              message: `Successfully deleted discount code: ${discountCode.code}`
            })
          );
        },
        onError: (error) => {
          dispatch(
            showModal('ALERT', {
              message: error?.message
            })
          );
        }
      }
    );
  };

  const handleOpenDeleteConfirmation = (discountCode: DiscountCodeDto) => {
    dispatch(
      showModal('CONFIRMATION', {
        message: `Are you sure you want to delete "${discountCode.code}"?`,
        onSuccess: ({ close }) => {
          handleDeleteDiscountCode(discountCode);
          close();
        }
      })
    );
  };

  // Make sure only organizers can access the page
  if (!isEventLoading && !event?.isOrganizer) {
    return <Redirect to="/404" />;
  }

  if (isLoading || isLoadingAnalytics || isEventLoading) {
    return (
      <div className="w-full md:w-3/5 lg:w-2/5 mx-auto mt-10">
        <DiscountCodeCardSkeleton />
        <DiscountCodeCardSkeleton />
        <DiscountCodeCardSkeleton />
      </div>
    );
  }

  if (error || getAnalyticsError) {
    dispatch(
      showModal('ALERT', {
        message: error?.message || getAnalyticsError
      })
    );
  }

  return (
    <>
      <FadeIn>
        <div className="relative w-full rounded-2xl p-4">
          <div
            className="absolute left-2 top-2 md:left-40 md:top-10 cursor-pointer"
            onClick={() => onBack()}
          >
            <BiLeftArrowAlt className="text-primary-500 text-4xl" />
          </div>

          {discountCodesResult?.discountCodes.length === 0 && (
            <DiscountCodeEmptyState
              handleAddDiscountCode={handleOpenCreateForm}
            />
          )}

          <div
            className={classNames('w-full md:w-3/5 lg:w-2/5 mx-auto mt-10', {
              'blur-sm': !!error?.message
            })}
          >
            {discountCodesResult?.discountCodes.map((discountCode) => (
              <DiscountCodeCard
                key={discountCode.id}
                discountCode={discountCode}
                analytics={analyticsMap[discountCode.id]}
                onEdit={handleUpdateDiscountCode}
                onDelete={handleOpenDeleteConfirmation}
              />
            ))}
          </div>
        </div>
      </FadeIn>

      <DiscountCodesActions handleAddDiscountCode={handleOpenCreateForm} />

      {shouldShowCreateForm && (
        <DiscountCodeForm
          eventId={eventId}
          onSubmit={(discountCode) => {
            handleCreateDiscountCode({
              code: discountCode.code,
              discount: {
                value: discountCode.discount.value,
                kind: discountCode.discount.kind as 'amount' | 'percentage'
              },
              ...(discountCode.maxUsage && { maxUsage: discountCode.maxUsage }),
              ...(discountCode.startDate && {
                startDate: discountCode.startDate
              }),
              ...(discountCode.endDate && { endDate: discountCode.endDate }),
              ...(discountCode.tierIds && { tierIds: discountCode.tierIds })
            });
          }}
          onClose={handleCloseCreateForm}
        />
      )}
    </>
  );
};

export default DiscountCodesPage;
